import { Grid } from '@mui/material'
import { Card, Box, ListItem, CardHeader } from 'components'
import { get } from 'lodash'
import {
  formatCurrency,
  formatDate,
  getTimezonedIsoString,
  isDivision60User
} from 'utils'
import { Order } from 'modules/orders/types'
import { useAuth } from 'lib/auth'

export const General = ({ order }: { order?: Order }) => {
  const { user } = useAuth()
  const canSeeCommission = isDivision60User(user?.division)

  const firstColumn: any = [
    {
      label: 'Cliente',
      key: 'customer',
      transform: (value: any) =>
        value?.id ? `${value.id} - ${value.name}` : '-'
    },
    {
      label: 'Representante',
      key: 'seller',
      transform: (value: any) =>
        value?.id ? `${value.id} - ${value.name}` : '-'
    },
    {
      label: 'Responsável pelo Pagamento',
      key: 'paymentResponsible',
      transform: (value: any) =>
        value?.id ? `${value.id} - ${value.name}` : '-'
    },
    // {
    //   label: 'Indicador',
    //   key: 'indicator',
    //   transform: (value: any) =>
    //     value?.id ? `${value.id} - ${value.name}` : '-'
    // },
    {
      label: 'Canal de Venda',
      key: 'saleChannel',
      transform: (value: any) =>
        value?.id ? `${value.id} - ${value.name}` : '-'
    }

    // {
    //   label: 'Observação NF',
    //   key: 'observation'
    // }
  ]

  const secondColumn = [
    {
      label: 'Divisão Cliente',
      key: 'customer',
      transform: (value: any) =>
        value?.division?.id
          ? `${value.division.id} - ${value.division.name}`
          : '-'
    },

    {
      label: 'Data do Orçamento',
      key: 'proposalDate',
      transform: (value: string) => formatDate(value)
    },
    {
      label: 'Data do Pedido',
      key: 'orderDate',
      transform: (value: string) => formatDate(value)
    },
    {
      label: 'Tipo de Venda',
      key: 'orderType',
      transform: (value: any) =>
        value?.id ? `${value.id} - ${value.name}` : '-'
    },
    {
      label: 'Unidade',
      key: 'unity',
      transform: (value: any) =>
        value?.id ? `${value.id} - ${value.name}` : '-'
    },
    {
      label: 'Observação do Pedido',
      key: 'observation',
      resolve: (o: Order) => o?.observation ?? '-'
    }
  ]

  const thirdColumn = [
    {
      label: 'Situação',
      key: 'status',
      transform: (value: any) =>
        value?.id ? `${value.id} - ${value.name}` : '-'
    },
    {
      label: 'Ordem de Compra',
      key: 'purchaseOrder'
    },
    {
      label: 'Número do Pedido do Representante',
      key: 'representantOrderNumber'
    },
    {
      label: 'Validade da Proposta',
      key: 'proposalValidity',
      transform: (value: string) => formatDate(getTimezonedIsoString(value))
    },
    {
      label: 'Pedido de Reserva',
      key: 'reserveOrder',
      transform: (value: any) => (value ? 'SIM' : 'NÃO')
    },
    {
      label: 'Pedido Reserva Vinculado',
      key: 'cdPedidoReserva',
      transform: (value: any) => value || '-'
    }
  ]

  const totals1 = [
    ...(canSeeCommission
      ? [
          {
            label: 'Comissão (%)',
            key: 'commissionPercent',
            transform: (value: number) => {
              const fallbackPercent = (order as any)?.totalCommission
              const percent =
                typeof value === 'number' && !Number.isNaN(value)
                  ? value
                  : Number(fallbackPercent || 0)
              return `${percent.toFixed(2)}%`
            }
          }
        ]
      : []),
    {
      label: 'Total Frete',
      key: 'totalFreight',
      transform: (value: number) => formatCurrency(value || 0)
    }
  ]

  const totals2 = [
    {
      label: 'Total de Desconto',
      key: 'totalDiscount',
      transform: (value: number) => formatCurrency(value || 0)
    },
    {
      label: 'Total de Acréscimo',
      key: 'totalExpenses',
      transform: (value: number) => formatCurrency(value || 0)
    }
  ]

  const totals3 = [
    {
      label: 'Total IPI',
      key: 'totalIpi',
      transform: (value: number) => formatCurrency(value || 0)
    },
    {
      label: 'Total ST',
      key: 'totalSt',
      transform: (value: number) => formatCurrency(value || 0)
    }
  ]

  const totals4 = [
    {
      label: 'Total Produtos',
      key: 'totalProducts',
      transform: (value: number) => formatCurrency(value || 0)
    },
    {
      label: 'Total Geral',
      key: 'totalOrder',
      transform: (value: number) => formatCurrency(value || 0)
    }
  ]

  return (
    <div>
      <Card
        sx={{
          visibility: { xs: 'hidden', sm: 'visible', md: 'visible' },
          padding: { xs: 0, sm: 0, md: 0 },
          maxWidth: 900
        }}
      >
        <Box
          sx={{ visibility: { xs: 'visible', sm: 'visible', md: 'visible' } }}
        >
          <Box
            sx={{
              padding: {
                xs: 0,
                sm: '0 24px 0 0',
                md: '0px 24px 0 0px'
              },
              borderBottom: { xs: 'none', sm: '1px solid #E0E0E0' }
            }}
          >
            <CardHeader title="Informações Gerais" />
          </Box>
          {/* <Divider
            sx={{ visibility: { xs: 'hidden', sm: 'visible', md: 'visible' } }}
          /> */}
          <Grid container spacing={3} style={{ padding: 24 }}>
            <Grid item xs={12} sm={4}>
              {firstColumn?.map((item: any) => (
                <ListItem
                  key={item.key}
                  label={item.label}
                  value={
                    item.resolve ? item.resolve(order) : get(order, item.key)
                  }
                  transform={item?.transform}
                  // transform={(value) => item?.transform(value, item)}
                />
              ))}
            </Grid>
            <Grid item xs={12} sm={4}>
              {secondColumn?.map((item: any) => (
                <ListItem
                  key={item.key}
                  label={item.label}
                  value={
                    item.resolve ? item.resolve(order) : get(order, item.key)
                  }
                  transform={item?.transform}
                />
              ))}
            </Grid>
            <Grid item xs={12} sm={4}>
              {thirdColumn?.map((item: any) => (
                <ListItem
                  key={item.key}
                  label={item.label}
                  value={get(order, item.key)}
                  transform={item?.transform}
                />
              ))}
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Card
        sx={{
          visibility: { xs: 'hidden', sm: 'visible', md: 'visible' },
          padding: { xs: 0, sm: 0, md: 0 },
          marginTop: 2,
          maxWidth: 900
        }}
      >
        <Box
          sx={{ visibility: { xs: 'visible', sm: 'visible', md: 'visible' } }}
        >
          <Box
            sx={{
              padding: {
                xs: 0,
                sm: '0 24px 0 0',
                md: '0px 24px 0 0px'
              },
              borderBottom: { xs: 'none', sm: '1px solid #E0E0E0' }
            }}
          >
            <CardHeader title="Totais" />
          </Box>
          {/* <Divider
            sx={{ visibility: { xs: 'hidden', sm: 'visible', md: 'visible' } }}
          /> */}
          <Grid container spacing={3} style={{ padding: 24 }}>
            <Grid item xs={12} sm={3}>
              {totals1?.map((item: any) => (
                <ListItem
                  key={item.key}
                  label={item.label}
                  value={get(order, item.key)}
                  transform={item?.transform}
                />
              ))}
            </Grid>
            <Grid item xs={12} sm={3}>
              {totals2?.map((item: any) => (
                <ListItem
                  key={item.key}
                  label={item.label}
                  value={get(order, item.key)}
                  transform={item?.transform}
                />
              ))}
            </Grid>
            <Grid item xs={12} sm={3}>
              {totals3?.map((item: any) => (
                <ListItem
                  key={item.key}
                  label={item.label}
                  value={get(order, item.key)}
                  transform={item?.transform}
                />
              ))}
            </Grid>
            <Grid item xs={12} sm={3}>
              {totals4?.map((item: any) => (
                <ListItem
                  key={item.key}
                  label={item.label}
                  value={get(order, item.key)}
                  transform={item?.transform}
                />
              ))}
            </Grid>
          </Grid>
        </Box>
      </Card>
    </div>
  )
}
